import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  LocationCity as DistrictIcon,
  Person as StudentIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Legend,
  ReferenceLine,
} from 'recharts';
import { zestpKpiAPI } from '../../services/zestpKpiApi';

const getStatusColor = (current: number, target: number, isLowerBetter = false) => {
  const ratio = isLowerBetter ? target / current : current / target;
  if (ratio >= 0.9) return '#4caf50';
  if (ratio >= 0.7) return '#ff9800';
  return '#f44336';
};

const getStatusLabel = (current: number, target: number, isLowerBetter = false) => {
  const ratio = isLowerBetter ? target / current : current / target;
  if (ratio >= 0.9) return 'On Track';
  if (ratio >= 0.7) return 'At Risk';
  return 'Off Track';
};

interface KpiCardProps {
  kpi: any;
}

const KpiCard: React.FC<KpiCardProps> = ({ kpi }) => {
  const isLowerBetter = kpi.id === 'teaching_quality';
  const progress = isLowerBetter
    ? Math.min(100, (kpi.target / kpi.current_value) * 100)
    : Math.min(100, (kpi.current_value / kpi.target) * 100);
  const statusColor = getStatusColor(kpi.current_value, kpi.target, isLowerBetter);
  const statusLabel = getStatusLabel(kpi.current_value, kpi.target, isLowerBetter);

  return (
    <Paper sx={{ p: 2.5, height: '100%', borderTop: `4px solid ${statusColor}` }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
          {kpi.name}
        </Typography>
        <Chip
          label={statusLabel}
          size="small"
          sx={{ bgcolor: statusColor, color: 'white', fontWeight: 600, fontSize: '0.65rem' }}
        />
      </Box>
      <Typography variant="h3" sx={{ fontWeight: 700, color: statusColor, mb: 0.5 }}>
        {kpi.current_value}{kpi.unit === 'percentage' ? '%' : kpi.unit === 'ratio' ? ':1' : ''}
      </Typography>
      <Box display="flex" gap={2} mb={1.5}>
        <Typography variant="caption" color="text.secondary">
          Baseline: <strong>{kpi.baseline}{kpi.unit === 'percentage' ? '%' : kpi.unit === 'ratio' ? ':1' : ''}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Target: <strong>{kpi.target}{kpi.unit === 'percentage' ? '%' : kpi.unit === 'ratio' ? ':1' : ''}</strong>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: '#e0e0e0',
          '& .MuiLinearProgress-bar': { bgcolor: statusColor, borderRadius: 4 },
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {kpi.source}
      </Typography>
      {kpi.by_gender && Object.keys(kpi.by_gender).length > 0 && (
        <Box mt={1.5} display="flex" gap={1.5}>
          {Object.entries(kpi.by_gender).map(([g, val]) => (
            <Chip
              key={g}
              label={`${g === 'M' ? 'Male' : g === 'F' ? 'Female' : g}: ${val}%`}
              size="small"
              variant="outlined"
              color={g === 'F' ? 'secondary' : 'primary'}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};

const ImplementationCard: React.FC<{ label: string; value: number; target?: number; icon: React.ReactNode }> = ({
  label,
  value,
  target,
  icon,
}) => (
  <Paper sx={{ p: 2, textAlign: 'center' }}>
    <Box sx={{ color: 'primary.main', mb: 1 }}>{icon}</Box>
    <Typography variant="h4" fontWeight={700}>
      {value.toLocaleString()}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {target && (
      <Typography variant="caption" color="text.secondary">
        Target: {target}
      </Typography>
    )}
  </Paper>
);

const ZestpKpiDashboard: React.FC = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['zestp-kpis'],
    queryFn: () => zestpKpiAPI.getKpiDashboard(),
    select: (res) => res.data,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load ZESTP KPI data. Please try again.</Alert>;
  }

  if (!data) return null;

  const { system_kpis, implementation_kpis, trends } = data;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            ZESTP KPI Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zanzibar Education Sector Transformation Plan — Key Performance Indicators ({data.academic_year})
          </Typography>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* System-Level KPIs */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        System-Level KPIs (ZESTP / GPE-aligned)
      </Typography>
      <Grid container spacing={2} mb={4}>
        {system_kpis.map((kpi: any) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={kpi.id}>
            <KpiCard kpi={kpi} />
          </Grid>
        ))}
      </Grid>

      {/* Implementation KPIs */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        Implementation KPIs
      </Typography>
      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} sm={3}>
          <ImplementationCard
            label="Schools in EMIS"
            value={implementation_kpis.schools_onboarded?.current_value || 0}
            icon={<SchoolIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ImplementationCard
            label="Teachers in System"
            value={implementation_kpis.teachers_in_system?.current_value || 0}
            icon={<PeopleIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ImplementationCard
            label="Districts Connected"
            value={implementation_kpis.districts_connected?.current_value || 0}
            target={implementation_kpis.districts_connected?.target}
            icon={<DistrictIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ImplementationCard
            label="Total Students"
            value={implementation_kpis.total_students?.current_value || 0}
            icon={<StudentIcon fontSize="large" />}
          />
        </Grid>
      </Grid>

      {/* Trends Chart */}
      {trends && trends.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} mb={2}>
            KPI Trends Over Time
          </Typography>
          <Paper sx={{ p: 3, mb: 4 }}>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis domain={[0, 100]} unit="%" />
                <RechartsTooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="primary_completion_rate"
                  stroke="#1976d2"
                  strokeWidth={2}
                  name="Primary Completion Rate"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="survival_to_f4"
                  stroke="#f44336"
                  strokeWidth={2}
                  name="Survival to F4"
                  dot={{ r: 4 }}
                />
                <ReferenceLine y={89.5} stroke="#1976d2" strokeDasharray="5 5" label="Target 89.5%" />
                <ReferenceLine y={70} stroke="#f44336" strokeDasharray="5 5" label="Target 70%" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      {/* District Breakdown */}
      {system_kpis[0]?.by_district && system_kpis[0].by_district.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} mb={2}>
            District-Level Performance
          </Typography>
          <Paper sx={{ p: 2 }}>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={system_kpis[0].by_district} layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} unit="%" />
                <YAxis type="category" dataKey="district" width={110} tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Bar dataKey="value" name="Primary Completion Rate" radius={[0, 4, 4, 0]}>
                  {system_kpis[0].by_district.map((entry: any, index: number) => (
                    <Cell key={index} fill={entry.value >= 89.5 ? '#4caf50' : entry.value >= 70 ? '#ff9800' : '#f44336'} />
                  ))}
                </Bar>
                <ReferenceLine x={89.5} stroke="#333" strokeDasharray="5 5" label="Target" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ZestpKpiDashboard;
